import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { AppShell } from '../components/AppShell';
import {
  ApiError,
  getSubscription,
  listGalleries,
  listPublicSubscriptionPlans,
  requestSubscriptionChange,
  updateProfile,
  type BillingInterval,
  type Photographer,
  type SubscriptionPlanId,
  type UpdateProfileInput
} from '../lib/api';
import { useAuth } from '../lib/auth';
import { DashboardSettingsForm } from './DashboardPage';

export function SettingsPage() {
  const { token } = useAuth();
  const queryClient = useQueryClient();
  const galleriesQuery = useQuery({ queryKey: ['galleries'], queryFn: () => listGalleries(token!), enabled: Boolean(token) });
  const subscriptionQuery = useQuery({ queryKey: ['subscription'], queryFn: () => getSubscription(token!), enabled: Boolean(token) });
  const plansQuery = useQuery({ queryKey: ['public-subscription-plans'], queryFn: listPublicSubscriptionPlans });
  const settingsMutation = useMutation({
    mutationFn: (input: UpdateProfileInput) => updateProfile(token!, input),
    onSuccess: ({ photographer }) => updatePhotographerCache(photographer),
    onError: (error) => {
      if (!(error instanceof ApiError) || error.details.code !== 'COGNITO_NAME_SYNC_FAILED') return;
      const savedPhotographer = error.details.photographer as Photographer | undefined;
      if (savedPhotographer?.photographerId) updatePhotographerCache(savedPhotographer);
    }
  });
  const subscriptionRequestMutation = useMutation({
    mutationFn: (input: { targetPlanId: Exclude<SubscriptionPlanId, 'trial'>; billingInterval: BillingInterval; note?: string }) => requestSubscriptionChange(token!, input),
    onSuccess: (subscription) => queryClient.setQueryData(['subscription'], subscription)
  });

  function updatePhotographerCache(photographer: Photographer) {
    queryClient.setQueryData<Awaited<ReturnType<typeof listGalleries>>>(['galleries'], (current) => current ? ({
      photographer,
      galleries: current.galleries.map((gallery) => ({ ...gallery, photographerSlug: photographer.slug }))
    }) : current);
  }

  return (
    <AppShell>
      <div className="mx-auto grid max-w-4xl gap-6">
        <div>
          <p className="text-sm font-semibold uppercase tracking-[.16em] text-ink/55">Account</p>
          <h1 className="mt-1 text-3xl font-semibold">Settings</h1>
          <p className="mt-2 text-sm text-ink/70">Manage your Storyly identity, notifications, and subscription.</p>
        </div>
        {!galleriesQuery.data ? (
          galleriesQuery.isError ? <p className="rounded-sm bg-clay/10 p-4 text-sm text-clay" role="alert">Settings could not be loaded.</p> : <div className="flex min-h-48 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin" aria-label="Loading settings" /></div>
        ) : (
          <section className="rounded-sm bg-white p-5 shadow-sm ring-1 ring-ink/10 sm:p-7">
            <DashboardSettingsForm
              photographer={galleriesQuery.data.photographer}
              subscription={subscriptionQuery.data}
              plans={plansQuery.data?.plans ?? []}
              subscriptionLoading={subscriptionQuery.isLoading || plansQuery.isLoading}
              subscriptionRequestPending={subscriptionRequestMutation.isPending}
              subscriptionRequestError={subscriptionRequestMutation.error}
              onRequestSubscription={subscriptionRequestMutation.mutate}
              isPending={settingsMutation.isPending}
              error={settingsMutation.error}
              identitySyncRequired={settingsMutation.error instanceof ApiError && settingsMutation.error.details.code === 'COGNITO_NAME_SYNC_FAILED'}
              onSubmit={settingsMutation.mutate}
            />
          </section>
        )}
      </div>
    </AppShell>
  );
}
